import isEmpty from 'lodash/isEmpty'
import castArray from 'lodash/castArray'
import each from 'lodash/each'
import includes from 'lodash/includes'
import qs from 'qs'
import Ncs from 'ncs';

const groupsConfig = {
  'ncs_texts.grupo_edad': {
    sortBy: v => ['<19', '19-34', '35-54', '>54', 'Desconocido'].indexOf(v)
  },
  'ncs_texts.estudios': {
    sortBy: v => ['Universitarios', 'Medios', 'Primarios', 'Desconocidos'].indexOf(v)
  }
}

const matchingExpressionsOrder = [
  { name: 'matching', criteria: Ncs.utils.matchingOrder(5, true) },
  { name: 'elements', criteria: [{ field: 'matching_expressions.elements', asc: false }] },
  ...Ncs.utils.elementFieldOrder(5, true, 'units', 'unit'),
  ...Ncs.utils.elementFieldOrder(5, true, 'tokens', 'token'),
  ...Ncs.utils.elementFieldOrder(5, true, 'tokens', 'tag'),
  ...Ncs.utils.elementFieldOrder(5, true, 'tokens', 'lemma'),
  ...Ncs.utils.elementFieldOrder(5, true, 'tokens', 'unit')
]

const documentTextOrder = [
  {
    name: 'documento',
    criteria: [
      { field: 'ncs_documents.nombre_fichero_audio', asc: true },
      { field: 'ncs_texts.index', asc: true },
    ]
  },
  { name: 'hablante', criteria: [{ field: 'ncs_texts.hablante', asc: true }] },
  { name: 'edad', criteria: [{ field: 'ncs_texts.edad', asc: true }] },
  { name: 'estudios', criteria: [{ field: 'ncs_texts.estudios', asc: true }] },
]

const matchingExpressionsConfig = {
  groups: groupsConfig,
  tokens: {
    groups: ['token', 'category', 'tag', 'lemma', 'unit'],
  },
  order: {
    orders: matchingExpressionsOrder,
    default: ['elements', 'matching']
  }
}

const collocationsConfig = {
  ...matchingExpressionsConfig,
  elements: {
    ...matchingExpressionsConfig.elements,
    types: ['tokens'],
    defaultType: 'tokens',
    setup: (state, dispatch, config) => {
      // Exclude:
      // Preposición (X)
      // Conjunción (C)
      // Determinantes (D)
      // Pronombre (P)
      // Interjección (I)
      // Signo de puntuación (Q)
      const collocationElement = { token: { value: '*' }, tag: { value: '!X!C!D*!P*!I!Q', tagSelectorUsed: 'pattern' } }
      const elements = { left: [collocationElement], right: [config.elements.emptyElement()] }
      dispatch('elements/setElements', elements, { root: true })
      dispatch('elements/setDistance', { type: 'lteq', number: 1 }, { root: true })
    },
    teardown: (state, dispatch, config) => {
      dispatch('elements/setElements', { left: [config.elements.emptyElement()], right: [] }, { root: true })
      dispatch('elements/setDistance', { type: null, number: 1 }, { root: true })
    }
  },
  tokens: {
    ...matchingExpressionsConfig.tokens,
    defaultGroups: ['lemma']
  },
}

const interventionsConfig = {
  endpoint: 'interventions',
  maxHeaderSize: 50, // Percentage of result table width
  contextOnRowClick: false,
  defaultDisplay: 'interventions',
  order: {
    orders: documentTextOrder,
    default: ['documento']
  },
  pagination: true,
  elements: {
    types: ['texts'],
    defaultType: 'texts'
  },
}

const interventionConfig = {
  endpoint: 'intervention',
  defaultDisplay: 'intervention',
  pagination: false,
  elements: {
    types: ['texts'],
    defaultType: 'texts'
  }
}

export default {
  elements: {
    types: ['units', 'tokens', 'texts']
  },
  categories: {
    patterns: {
      A: 'A*',
      W: 'W*',
      C: 'C*',
      D: 'D*',
      I: 'I*',
      X: 'X*',
      P: 'P*',
      Q: 'Q*',
      N: 'N*',
      V: 'V*',
      ETQ_PAUSA: 'ETQ_PAUSA',
    }
  },
  tags: {
    categories: gon.tag_categories,
    matrix: gon.tag_matrix,
    hideLevels: ['noun_class', 'noun_subclass', 'noun_grade']
  },
  filters: {
    values: gon.values,
    dependencyValues: gon.dependencyValues
  },
  result: {
    defaultType: 'kwic',
    error: {
      contactLink: './contacto',
      contactEmail: 'support@nlpgo.com'
    },
    types: ['frequency', 'full_frequency', 'kwic', 'matching_expressions', 'interventions'],
    detailTypes: ['context', 'intervention'],
    kwic: {
      order: {
        orders: [...Ncs.utils.defaultOrder(5), ...documentTextOrder],
        default: ['matching', 'documento']
      }
    },
    full_frequency: {
      groups: groupsConfig
    },
    matching_expressions: matchingExpressionsConfig,
    collocations: collocationsConfig,
    context: {
      textHeader: true,
      tokens: { sourceText: true }
    },
    interventions: interventionsConfig,
    intervention: interventionConfig,
  },
  download: {
    interventions: [
      {
        name: 'download.page.csv',
        type: 'Page',
        mimeType: 'text/csv',
        filename: 'result.csv',
        pageViewName: 'download'
      },
      {
        name: 'download.page.xlsx',
        type: 'Page',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        filename: 'result.xlsx',
        pageViewName: 'download'
      },
    ]
  },
  queryLinks: {
    enabled: true,
    stringifyQuery(q, _store) {
      return ''
    },
    parseQuery(s, store) {
      if (isEmpty(s)) return

      const parsed = qs.parse(s, { arrayFormat: 'repeat', decoder: Ncs.utils.decoder })
      const frequencyGroups = castArray(parsed.fg)

      store.dispatch('api/changeResultType', 'matching_expressions')
      store.dispatch('elements/setSearchType', { type: 'tokens' })
      store.dispatch('elements/setElements', {
        left: [{ token: { value: '*' } }],
        right: [Ncs.config.elements.emptyElement()]
      })
      store.dispatch('groups/setGroups', parsed.g)
      const allFrequencyGroupValues = Ncs.config.result.matching_expressions.frequency_group_values
      each(allFrequencyGroupValues, (values, group) => {
        if (includes(frequencyGroups, group)) {
          store.commit('frequency_groups/setFrequencyGroupValues', { group, values })
        }
      })
      store.commit('frequency_groups/setFrequencyValues', ['frequency', 'normalized'])
      store.dispatch('api/execute')
    }
  }
}
